import { Dialog } from "@mui/material";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { TbAlertCircle, TbArrowLeft, TbX } from "react-icons/tb";
import { useAccountData } from "../../../Hooks";
import { useFidoPilotOnboarding } from "../../../Hooks/useFidoPilotOnboarding";
import { useFidoStepUp } from "../../../Hooks/useFidoStepUp";
import { FidoTouchPanel } from "../FidoTouchPanel";

const generateKeypadPairs = () => {
  const digits = [];
  const used = new Set();

  while (digits.length < 10) {
    const n = Math.floor(Math.random() * 10);
    if (!used.has(n)) {
      used.add(n);
      digits.push(n);
    }
  }

  return [
    `${digits[0]} - ${digits[1]}`,
    `${digits[2]} - ${digits[3]}`,
    `${digits[4]} - ${digits[5]}`,
    `${digits[6]} - ${digits[7]}`,
    `${digits[8]} - ${digits[9]}`,
  ];
};

const DIGIT_KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"];

const digitButtonClass =
  "flex h-12 w-full items-center justify-center rounded-xl border border-gray-200 bg-white text-base font-semibold text-brand-dark transition hover:border-[#e7a73f] hover:bg-brand-cream focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e7a73f]/40 sm:h-14 sm:text-lg";

export const PasswordConfirm = forwardRef(function PasswordConfirm(
  {
    open,
    submitPassword,
    onClose,
    onBack,
    error = null,
    loading = false,
    onDismissError,
    variant = "modal",
    hideFooter = false,
    fidoStepUp: fidoStepUpProp,
    stepUpOptions = null,
    /** "confirm" = pair keypad; "register" = 0-9 digits entered twice */
    mode = "confirm",
    /** Optional title/subtitle overrides (e.g. settings change flow) */
    title: titleOverride = null,
    subtitle: subtitleOverride = null,
  },
  ref
) {
  const isRegister = mode === "register";
  const isInline = variant === "inline";
  const isEmbedded = variant === "embedded";
  const { state: accountData } = useAccountData();
  const fidoPilot = useFidoPilotOnboarding(accountData);
  const internalFidoStepUp = useFidoStepUp(fidoPilot, submitPassword, stepUpOptions ?? undefined);
  const fidoStepUp = fidoStepUpProp ?? internalFidoStepUp;
  const [keypadPairs, setKeypadPairs] = useState(() => generateKeypadPairs());
  const [passwordCombination, setPasswordCombination] = useState([]);
  const [authMode, setAuthMode] = useState("password");
  const [registerPhase, setRegisterPhase] = useState("create"); // create | confirm
  const [pendingPassword, setPendingPassword] = useState("");
  const [localError, setLocalError] = useState(null);

  const resetInputs = useCallback(() => {
    setKeypadPairs(generateKeypadPairs());
    setPasswordCombination([]);
    setAuthMode("password");
    setRegisterPhase("create");
    setPendingPassword("");
    setLocalError(null);
  }, []);

  // Reset keypad when the surface opens — not on every mode flip (register↔confirm),
  // which remounts /me and caused PIX confirm↔password flicker.
  useEffect(() => {
    if (isInline || isEmbedded || open) {
      resetInputs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally omit `mode`
  }, [open, isInline, isEmbedded, resetInputs]);

  useEffect(() => {
    if (error) {
      setPasswordCombination([]);
      setKeypadPairs(generateKeypadPairs());
      if (isRegister) {
        setRegisterPhase("create");
        setPendingPassword("");
      }
    }
  }, [error, isRegister]);

  const displayError = localError || error;

  const handleDigitPress = useCallback(
    (value) => {
      if (displayError) {
        setLocalError(null);
        onDismissError?.();
      }
      setPasswordCombination((prev) => {
        if (prev.length >= 4) return prev;
        return [...prev, value.replace(/\s+/g, "")];
      });
    },
    [displayError, onDismissError]
  );

  const handleClear = useCallback(() => {
    setKeypadPairs(generateKeypadPairs());
    setPasswordCombination([]);
    setLocalError(null);
  }, []);

  const handleConfirm = useCallback(() => {
    if (passwordCombination.length < 4 || loading) return;

    if (isRegister) {
      const digits = passwordCombination.join("");
      if (registerPhase === "create") {
        setPendingPassword(digits);
        setPasswordCombination([]);
        setRegisterPhase("confirm");
        setLocalError(null);
        return;
      }

      if (digits !== pendingPassword) {
        setLocalError({
          title: intl.get("passwordConfirm.warning"),
          description: intl.get("passwordConfirm.passwordsMustMatch"),
        });
        setPasswordCombination([]);
        setPendingPassword("");
        setRegisterPhase("create");
        return;
      }

      submitPassword({ password: pendingPassword, confirmPassword: digits });
      return;
    }

    submitPassword(passwordCombination);
  }, [
    passwordCombination,
    submitPassword,
    loading,
    isRegister,
    registerPhase,
    pendingPassword,
  ]);

  useImperativeHandle(
    ref,
    () => ({
      confirm: handleConfirm,
    }),
    [handleConfirm]
  );

  const handleClose = () => {
    resetInputs();
    onClose?.();
  };

  const confirmLabel = useMemo(() => {
    if (loading) {
      return isRegister
        ? intl.get("passwordConfirm.registeringPassword")
        : (intl.get("passwordConfirm.sendingPix") || "Enviando Pix…");
    }
    if (isRegister) {
      return registerPhase === "create"
        ? intl.get("passwordConfirm.registerPassword")
        : intl.get("passwordConfirm.confirmPassword");
    }
    return intl.get("passwordConfirm.confirmPassword");
  }, [loading, isRegister, registerPhase]);

  const titleText = useMemo(() => {
    if (titleOverride) return titleOverride;
    if (authMode === "fido") {
      return isInline
        ? intl.get("passwordConfirm.authorizeWithUsbKeyTitle")
        : intl.get("passwordConfirm.modalFidoTitle");
    }
    if (isRegister) {
      if (registerPhase === "confirm") {
        return intl.get("passwordConfirm.confirmRegisteredPassword");
      }
      return isInline || isEmbedded
        ? intl.get("passwordConfirm.registerTransactionPassword")
        : intl.get("passwordConfirm.modalRegisterTitle");
    }
    return isInline
      ? intl.get("passwordConfirm.confirmTransactionPassword")
      : intl.get("passwordConfirm.modalPasswordTitle");
  }, [titleOverride, authMode, isRegister, registerPhase, isInline, isEmbedded]);

  const subtitleText = useMemo(() => {
    if (subtitleOverride) return subtitleOverride;
    if (authMode === "fido") return intl.get("passwordConfirm.fidoTouchHint");
    if (isRegister) {
      return registerPhase === "confirm"
        ? intl.get("passwordConfirm.confirmRegisterDigitsHint")
        : intl.get("passwordConfirm.registerDigitsHint");
    }
    return intl.get("passwordConfirm.passwordDigitsHint");
  }, [subtitleOverride, authMode, isRegister, registerPhase]);

  const confirmDisabled = passwordCombination.length < 4 || loading;
  const showFidoOption = !isRegister && Boolean(fidoStepUp?.enabled);

  const keypadButtons = isRegister ? (
    <div className="mx-auto grid w-full max-w-full grid-cols-3 gap-2 sm:max-w-[18rem] sm:gap-3">
      {DIGIT_KEYS.slice(0, 9).map((digit) => (
        <button
          key={digit}
          type="button"
          className={digitButtonClass}
          onClick={() => handleDigitPress(digit)}
          disabled={passwordCombination.length >= 4 || loading}
        >
          {digit}
        </button>
      ))}
      <button
        type="button"
        className="flex h-12 w-full items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500 transition hover:border-red-200 hover:bg-red-50 hover:text-red-600 sm:h-14 sm:text-sm"
        onClick={handleClear}
        disabled={loading}
      >
        {intl.get("words.clear")}
      </button>
      <button
        type="button"
        className={digitButtonClass}
        onClick={() => handleDigitPress("0")}
        disabled={passwordCombination.length >= 4 || loading}
      >
        0
      </button>
      <div className="h-12 sm:h-14" aria-hidden />
    </div>
  ) : (
    <div className="mx-auto grid w-full max-w-full grid-cols-3 gap-2 sm:max-w-[18rem] sm:gap-3">
      {keypadPairs.map((pair) => (
        <button
          key={pair}
          type="button"
          className={digitButtonClass}
          onClick={() => handleDigitPress(pair)}
          disabled={passwordCombination.length >= 4 || loading}
        >
          {pair}
        </button>
      ))}
      <button
        type="button"
        className="flex h-12 w-full items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500 transition hover:border-red-200 hover:bg-red-50 hover:text-red-600 sm:h-14 sm:text-sm"
        onClick={handleClear}
        disabled={loading}
      >
        {intl.get("words.clear")}
      </button>
    </div>
  );

  const passwordBody = authMode === "fido" ? (
    <FidoTouchPanel
      onWebAuthn={fidoStepUp?.onWebAuthn}
      onSuccess={fidoStepUp?.onConfirm}
      disabled={loading}
      idleMessage={intl.get("passwordConfirm.fidoIdleMessage")}
      successMessage={intl.get("passwordConfirm.operationAuthorized")}
      buttonLabel={intl.get("passwordConfirm.authorizeWithUsbKey")}
    />
  ) : (
    <>
      {displayError && (
        <div className="mb-5 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600">
            <TbAlertCircle className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-red-600">
              {displayError.title || intl.get("passwordConfirm.warning")}
            </p>
            {displayError.description ? (
              <p className="mt-0.5 text-sm text-red-600">{displayError.description}</p>
            ) : null}
          </div>
        </div>
      )}

      <div className="mb-6 flex items-center justify-center gap-3">
        {[0, 1, 2, 3].map((index) => (
          <div
            key={index}
            className={`h-3.5 w-3.5 rounded-full transition ${
              index < passwordCombination.length
                ? "bg-brand-dark scale-110"
                : "border-2 border-gray-300 bg-white"
            }`}
          />
        ))}
      </div>

      {keypadButtons}

      <p className="mt-4 text-center text-xs text-gray-400">
        {isRegister
          ? intl.get("passwordConfirm.registerKeypadHint")
          : intl.get("passwordConfirm.keypadHint")}
      </p>
    </>
  );

  const fidoToggle = showFidoOption ? (
    <div className="mb-4 flex justify-center">
      <div
        className="stables-segment-control inline-flex"
        role="tablist"
        aria-label={intl.get("passwordConfirm.authMethodLabel")}
      >
        <button
          type="button"
          role="tab"
          aria-selected={authMode === "password"}
          onClick={() => setAuthMode("password")}
          disabled={loading}
          className={`rounded-md px-4 py-2 text-xs font-semibold transition sm:px-5 ${
            authMode === "password"
              ? "bg-[#e7a73f] text-white shadow-sm"
              : "text-gray-500 hover:text-brand-dark dark:text-gray-400 dark:hover:text-gray-200"
          }`}
        >
          {intl.get("words.password")}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={authMode === "fido"}
          onClick={() => setAuthMode("fido")}
          disabled={loading}
          className={`rounded-md px-4 py-2 text-xs font-semibold transition sm:px-5 ${
            authMode === "fido"
              ? "bg-[#e7a73f] text-white shadow-sm"
              : "text-gray-500 hover:text-brand-dark dark:text-gray-400 dark:hover:text-gray-200"
          }`}
        >
          {intl.get("words.usbKey")}
        </button>
      </div>
    </div>
  ) : null;

  const embeddedFooter = (
    <div className="border-t border-gray-100 px-5 py-4 sm:px-6">
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
        <button
          type="button"
          className="btn-back w-full sm:w-auto"
          onClick={onBack}
          disabled={loading}
        >
          <TbArrowLeft className="h-4 w-4" />
          {intl.get("words.back")}
        </button>
        {authMode === "password" ? (
          <button
            type="button"
            className="btn-primary w-full sm:w-auto"
            disabled={confirmDisabled}
            onClick={handleConfirm}
          >
            {confirmLabel}
          </button>
        ) : null}
      </div>
    </div>
  );

  if (isEmbedded) {
    return (
      <>
        <div className="relative flex items-center justify-center bg-gradient-to-r from-[#fcd177] to-[#e7a73f] px-10 py-4">
          <p className="text-center text-sm font-semibold leading-snug text-white sm:text-[0.9375rem]">
            {titleText}
          </p>
        </div>

        <div className="px-5 py-6 sm:px-6">
          {fidoToggle}
          {passwordBody}
        </div>

        {embeddedFooter}
      </>
    );
  }

  if (isInline) {
    return (
      <section className="pix-transfer-section app-card min-w-0 overflow-x-hidden">
        <h2 className="pix-transfer-section__title">{titleText}</h2>
        <p className="pix-transfer-section__subtitle">{subtitleText}</p>
        <div className="mt-5">
          {fidoToggle}
          {passwordBody}
        </div>

        {!hideFooter && (
          <div className="mt-6 flex flex-col-reverse gap-2 border-t border-gray-100 pt-5 sm:flex-row sm:justify-between">
            <button
              type="button"
              className="btn-back w-full sm:w-auto"
              onClick={onBack}
              disabled={loading}
            >
              <TbArrowLeft className="h-4 w-4" />
              {intl.get("words.back")}
            </button>
            {authMode === "password" ? (
              <button
                type="button"
                className="btn-primary w-full sm:w-auto"
                disabled={confirmDisabled}
                onClick={handleConfirm}
              >
                {confirmLabel}
              </button>
            ) : null}
          </div>
        )}
      </section>
    );
  }

  const content = (
    <>
      <div className="relative flex items-center justify-center bg-gradient-to-r from-[#fcd177] to-[#e7a73f] px-10 py-4">
        <p className="text-center text-sm font-semibold leading-snug text-white sm:text-[0.9375rem]">
          {titleText}
        </p>
        <button
          type="button"
          onClick={handleClose}
          className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-white transition hover:bg-white/20"
          aria-label={intl.get("words.close")}
        >
          <TbX className="h-5 w-5" />
        </button>
      </div>

      <div className="px-5 py-6 sm:px-6">
        {fidoToggle}
        {passwordBody}
      </div>

      {authMode === "password" ? (
        <div className="border-t border-gray-100 px-5 py-4 sm:px-6">
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              className="btn-primary w-full sm:w-auto"
              disabled={confirmDisabled}
              onClick={handleConfirm}
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      ) : null}
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ className: "!rounded-2xl !shadow-card overflow-hidden" }}
    >
      {content}
    </Dialog>
  );
});
